if (!customElements.get('chapter-menu')) {
  customElements.define('chapter-menu', class ChapterMenu extends HTMLElement {
    connectedCallback() {
      this.boundTargets = new Set();
      this.generatedAnchors = new Set();
      this.handleClick = this.handleClick.bind(this);
      this.handleThemeEditorChange = this.scheduleBind.bind(this);
      this.handleResize = this.scheduleBind.bind(this);

      this.addEventListener('click', this.handleClick, true);
      window.addEventListener('resize', this.handleResize);

      if (Shopify.designMode) {
        document.addEventListener('shopify:section:load', this.handleThemeEditorChange);
        document.addEventListener('shopify:section:reorder', this.handleThemeEditorChange);
      }

      this.bindChapters();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick, true);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('shopify:section:load', this.handleThemeEditorChange);
      document.removeEventListener('shopify:section:reorder', this.handleThemeEditorChange);
      window.cancelAnimationFrame(this.bindFrame);
      this.clearTargets();
    }

    scheduleBind() {
      window.cancelAnimationFrame(this.bindFrame);
      this.bindFrame = window.requestAnimationFrame(() => this.bindChapters());
    }

    clearTargets() {
      this.boundTargets?.forEach((target) => {
        if (target.dataset.chapterMenuOwner !== this.menuSectionId) return;
        target.classList.remove('chapter-menu-target');
        target.style.removeProperty('--chapter-menu-scroll-margin-top');
        delete target.dataset.chapterMenuOwner;
      });
      this.boundTargets = new Set();

      this.generatedAnchors?.forEach((anchor) => anchor.remove());
      this.generatedAnchors = new Set();
    }

    isSectionUnavailable(section, checkEmpty) {
      if (!section?.id || section.hidden || section.getAttribute('aria-hidden') === 'true') return true;

      const sectionStyle = window.getComputedStyle(section);
      if (sectionStyle.display === 'none' || sectionStyle.visibility === 'hidden') return true;

      const ignoredTags = new Set(['LINK', 'SCRIPT', 'STYLE', 'TEMPLATE', 'NOSCRIPT']);
      const renderedChildren = Array.from(section.children).filter((child) => {
        if (ignoredTags.has(child.tagName) || child.hidden || child.getAttribute('aria-hidden') === 'true') return false;
        const style = window.getComputedStyle(child);
        return style.display !== 'none' && style.visibility !== 'hidden';
      });

      if (renderedChildren.length === 0) return true;
      if (!checkEmpty) return false;

      return !renderedChildren.some((child) => {
        if (child.textContent.trim() !== '') return true;
        return Boolean(child.querySelector('img, picture, video, iframe, svg, canvas, form, input, button, a[href], details, dl, table'));
      });
    }

    resolveAnchorTarget(link, index, targetSection) {
      const requestedAnchor = link.dataset.chapterAnchor?.trim() || 'chapter-' + (index + 1);
      const existingTarget = document.getElementById(requestedAnchor);
      const belongsToTarget = existingTarget && (existingTarget === targetSection || targetSection.contains(existingTarget));
      const ownedByAnotherMenu = existingTarget?.dataset.chapterMenuOwner
        && existingTarget.dataset.chapterMenuOwner !== this.menuSectionId;

      if (belongsToTarget && !ownedByAnotherMenu) return existingTarget;

      let anchorId = requestedAnchor;
      let suffix = index + 1;
      while (document.getElementById(anchorId)) {
        anchorId = requestedAnchor + '-' + suffix;
        suffix += 1;
      }

      const anchor = document.createElement('span');
      anchor.id = anchorId;
      anchor.className = 'chapter-menu-anchor';
      anchor.setAttribute('aria-hidden', 'true');
      targetSection.before(anchor);
      this.generatedAnchors.add(anchor);

      return anchor;
    }

    bindChapters() {
      const menuSection = this.closest('.shopify-section');
      if (!menuSection) return;

      this.menuSectionId = menuSection.id;
      this.clearTargets();

      const links = Array.from(this.querySelectorAll('[data-chapter-index]'));
      const hideEmptyChapters = this.dataset.hideEmptyChapters === 'true';
      const hideMenuWhenEmpty = this.dataset.hideMenuWhenEmpty === 'true';
      const scrollMarginTop = Math.max(Number.parseInt(this.dataset.scrollMarginTop, 10) || 0, 0);
      const sections = [];
      let sibling = menuSection.nextElementSibling;

      while (sibling && sections.length < links.length) {
        if (sibling.classList.contains('shopify-section')) sections.push(sibling);
        sibling = sibling.nextElementSibling;
      }

      links.forEach((link, index) => {
        const targetSection = sections[index];
        const shouldHide = !targetSection || this.isSectionUnavailable(targetSection, hideEmptyChapters);

        if (shouldHide) {
          link.hidden = true;
          link.removeAttribute('href');
          link.removeAttribute('aria-controls');
          return;
        }

        const anchorTarget = this.resolveAnchorTarget(link, index, targetSection);

        link.hidden = false;
        link.href = '#' + anchorTarget.id;
        link.setAttribute('aria-controls', targetSection.id);
        anchorTarget.classList.add('chapter-menu-target');
        anchorTarget.style.setProperty('--chapter-menu-scroll-margin-top', scrollMarginTop + 'px');
        anchorTarget.dataset.chapterMenuOwner = menuSection.id;
        this.boundTargets.add(anchorTarget);
      });

      if (hideMenuWhenEmpty && !Shopify.designMode) {
        menuSection.hidden = !links.some((link) => !link.hidden);
      } else {
        menuSection.hidden = false;
      }
    }

    handleClick(event) {
      const link = event.target.closest('[data-chapter-index]');
      if (!link || !this.contains(link) || link.hidden || this.dataset.smoothScroll !== 'true') return;

      const targetId = link.hash.slice(1);
      const target = document.getElementById(targetId);
      if (!target) return;

      event.preventDefault();
      event.stopPropagation();

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = reduceMotion ? 'auto' : 'smooth';

      if (window.location.hash === link.hash) {
        window.history.replaceState(null, '', link.hash);
      } else {
        window.history.pushState(null, '', link.hash);
      }

      target.scrollIntoView({ behavior, block: 'start' });
    }
  });
}
